import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
} from '@mui/material';
import { CheckCircle, Cancel, Person } from '@mui/icons-material';
import { userService } from '../services/userService';
import { formatServerDateTime } from '../utils/dateUtils';
import { useFeedback } from '../contexts/FeedbackContext';
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { PendingUserListItem, UserApprovalRequest, UserRejectionRequest } from '../types/user';

interface UserApprovalProps {
  /**
   * [Web UX 4. csomag (Admin teendők), 2026-10-04] A lista ÚJ hossza minden sikeres betöltés után (jóváhagyás/elutasítás is
   * újratölt) — így a Beállítások-jelvény abban a pillanatban egyezik a listával, újratöltés nélkül.
   */
  onPendingCountChange?: (count: number) => void;
}

const UserApproval: React.FC<UserApprovalProps> = ({ onPendingCountChange }) => {
  const { showSuccess } = useFeedback();
  const [pendingUsers, setPendingUsers] = useState<PendingUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PendingUserListItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState<string>('');
  /**
   * [7. javító kör — validátor F3, 2026-10-04] Az elutasítás hibája a NYITVA maradó dialógusban is
   * látszik (a `CoachPaymentManagement` `failCreate` mintája). KORÁBBAN csak a lap tetejei sávba
   * került (`error`), ami a modális dialógus MÖGÖTT, takarásban volt — a felhasználó a gomb
   * megnyomása után semmilyen visszajelzést nem látott.
   */
  const [rejectError, setRejectError] = useState<string>('');

  useEffect(() => {
    loadPendingUsers();
  }, []);

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const users = await userService.getPendingUsers();
      setPendingUsers(users);
      onPendingCountChange?.(users.length);
    } catch (err: any) {
      // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
      // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
      setError(requestErrorMessage(err, 'Hiba történt a jóváhagyásra váró felhasználók betöltésekor.'));
      console.error('Error loading pending users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      setActionLoading(userId);
      const approvalData: UserApprovalRequest = { userId };
      await userService.approveUser(approvalData);
      showSuccess('A regisztráció jóváhagyva.');
      await loadPendingUsers();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a jóváhagyás során.'));
      console.error('Error approving user:', err);
    } finally {
      setActionLoading('');
    }
  };

  const handleRejectUser = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(selectedUser.userId);
      setRejectError('');
      const rejectionData: UserRejectionRequest = {
        userId: selectedUser.userId,
        reason: rejectionReason
      };
      await userService.rejectUser(rejectionData);
      showSuccess('A regisztráció elutasítva.');
      setRejectDialogOpen(false);
      setSelectedUser(null);
      setRejectionReason('');
      await loadPendingUsers();
    } catch (err: any) {
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat; egyébként a
      // szerver magyar indoklása / a státuszkód (a régi fix mondat mindent összemosott).
      const message = saveErrorMessage(err, 'Hiba történt az elutasítás során.');
      setError(message);
      // [7. javító kör, F3] a dialógusban is (a lap tetejei sáv a dialógus mögött van)
      setRejectError(message);
      console.error('Error rejecting user:', err);
    } finally {
      setActionLoading('');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) return;

    try {
      setActionLoading('bulk');
      await userService.bulkApproveUsers({ userIds: selectedUsers });
      showSuccess(`${selectedUsers.length} regisztráció jóváhagyva.`);
      setSelectedUsers([]);
      await loadPendingUsers();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a tömeges jóváhagyás során.'));
      console.error('Error bulk approving users:', err);
    } finally {
      setActionLoading('');
    }
  };

  const openRejectDialog = (user: PendingUserListItem) => {
    setSelectedUser(user);
    setRejectError('');
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedUser(null);
    setRejectionReason('');
    setRejectError('');
  };

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Lelet: `UserApproval.tsx:287-290`
   * (korábbi sorok) — a beírt „Elutasítás oka” a háttér-kattintásra/Esc-re/„Mégse”-re kérdés
   * nélkül elveszett. Piszkos = nem üres (trim után); az üres mező NEM kérdez.
   * A hook a `loading` korai visszatérése ELŐTT áll.
   */
  const confirm = useConfirm();
  const reasonDirty = rejectDialogOpen && isFormDirty({ reason: '' }, { reason: rejectionReason });
  useUnsavedChanges(reasonDirty, 'Elutasítás oka');

  const requestCloseRejectDialog = async () => {
    if (selectedUser && actionLoading === selectedUser.userId) return;
    if (reasonDirty && !(await confirm(discardChangesConfirm('elutasítás oka')))) return;
    closeRejectDialog();
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === pendingUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(pendingUsers.map(u => u.userId));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Jóváhagyásra váró felhasználók betöltése...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Regisztrációk jóváhagyása
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {pendingUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nincsenek jóváhagyásra váró regisztrációk
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Minden regisztráció feldolgozásra került.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Jóváhagyásra váró felhasználók ({pendingUsers.length})
            </Typography>
            {selectedUsers.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleBulkApprove}
                disabled={actionLoading === 'bulk'}
                startIcon={actionLoading === 'bulk' ? <CircularProgress size={20} /> : <CheckCircle />}
              >
                {selectedUsers.length} felhasználó jóváhagyása
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.length === pendingUsers.length && pendingUsers.length > 0}
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < pendingUsers.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Név</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Szerep</TableCell>
                  <TableCell>Regisztráció dátuma</TableCell>
                  <TableCell align="center">Műveletek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.includes(user.userId)}
                        onChange={() => handleSelectUser(user.userId)}
                      />
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.userRoleDisplayName}
                        size="small"
                        color="default"
                      />
                    </TableCell>
                    <TableCell>
                      {/* [Web UX 1. csomag (4.3), 2026-10-03] a `createdDate` UTC, „Z” nélkül (`User.cs:145`) → közös segéd. */}
                      {formatServerDateTime(user.createdDate, { fallback: 'Ismeretlen dátum' })
                      }
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApproveUser(user.userId)}
                          disabled={actionLoading === user.userId}
                          startIcon={actionLoading === user.userId ? <CircularProgress size={16} /> : <CheckCircle />}
                        >
                          Jóváhagy
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => openRejectDialog(user)}
                          disabled={actionLoading === user.userId}
                          startIcon={<Cancel />}
                        >
                          Elutasít
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={requestCloseRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Regisztráció elutasítása
        </DialogTitle>
        <DialogContent>
          {rejectError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="reject-dialog-error">
              {rejectError}
            </Alert>
          )}
          <Typography variant="body1" sx={{ mb: 2 }}>
            Biztosan el szeretné utasítani <strong>{selectedUser?.name}</strong> regisztrációját?
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Elutasítás oka (opcionális)"
            type="text"
            fullWidth
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Adja meg az elutasítás okát..."
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseRejectDialog}>
            Mégse
          </Button>
          <Button
            onClick={handleRejectUser}
            color="error"
            variant="contained"
            disabled={actionLoading === selectedUser?.userId}
            startIcon={actionLoading === selectedUser?.userId ? <CircularProgress size={16} /> : <Cancel />}
          >
            Elutasít
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserApproval;